// Daily notes helpers (E11): pure functions for the editor and panels.
// Note syntax and resolution rules are in docs/crm/api.md "E11: daily notes".

const YMD = /^\d{4}-\d{2}-\d{2}$/;

export const isDate = (s) => YMD.test(s || "");

export const notePath = (date) => (date ? `/notes/${date}` : "/notes");

export const entityPath = (type, id) => (type === "company" ? `/crm/companies/${id}` : `/crm/contacts/${id}`);

// The link being typed at the caret, or null:
//  "[[Ay|"  -> { kind: "wiki", start: index of "[[", query: "Ay" }
//  "@Ay|"   -> { kind: "at", start: index of "@", query: "Ay" }
// "@" counts only at the start of a line or after a space or "(" (so
// e-mail addresses do not trigger it); "[[" only while unclosed on the line.
export function linkTrigger(text, caret) {
    if (typeof text !== "string" || caret == null || caret < 0) return null;
    const before = text.slice(0, caret);
    const lineStart = before.lastIndexOf("\n") + 1;
    const line = before.slice(lineStart);
    const open = line.lastIndexOf("[[");
    if (open !== -1) {
        const rest = line.slice(open + 2);
        if (!rest.includes("]]") && !rest.includes("[") && rest.length <= 60) {
            return { kind: "wiki", start: lineStart + open, query: rest };
        }
    }
    const m = /(^|[\s(])@([^\s@[\]()]{0,40})$/.exec(line);
    if (m) {
        const start = lineStart + m.index + m[1].length;
        return { kind: "at", start, query: m[2] };
    }
    return null;
}

// Replaces the trigger (from its start to the caret) with "[[name]] ".
// Returns the new text and caret position.
export function applyLink(text, trigger, caret, name) {
    let end = caret;
    // A "[[" link may already have its closing brackets after the caret.
    if (trigger.kind === "wiki" && text.slice(caret, caret + 2) === "]]") end = caret + 2;
    const insert = `[[${name}]] `;
    const out = text.slice(0, trigger.start) + insert + text.slice(end).replace(/^ /, "");
    return { text: out, caret: trigger.start + insert.length };
}

// Mentions grouped by record, in note order: [{key, entity_type, entity_id,
// name, items: [mention]}].
export function groupMentions(mentions) {
    const groups = [];
    const byKey = {};
    (mentions || []).forEach((m) => {
        const key = `${m.entity_type}:${m.entity_id}`;
        if (!byKey[key]) {
            byKey[key] = { key, entity_type: m.entity_type, entity_id: m.entity_id, name: m.name, hidden_at: m.hidden_at || null, items: [] };
            groups.push(byKey[key]);
        }
        // One row per line: two links to the same record on a line are one item.
        if (!byKey[key].items.some((x) => x.line_no === m.line_no)) byKey[key].items.push(m);
    });
    return groups;
}

// First n characters of a note for previews (whitespace collapsed per line).
export function notePreview(text, n = 240) {
    const t = String(text || "").replace(/[ \t]+/g, " ").trim();
    if (t.length <= n) return t;
    return t.slice(0, n - 1).trimEnd() + "…";
}

// Date list for the left column: the notes plus today and the open date,
// newest first, without duplicates.
export function noteDates(notes, today, current) {
    const seen = {};
    const out = [];
    (notes || []).forEach((n) => {
        if (n && isDate(n.date) && !seen[n.date]) {
            seen[n.date] = true;
            out.push(n);
        }
    });
    [today, current].forEach((d) => {
        if (isDate(d) && !seen[d]) {
            seen[d] = true;
            out.push({ date: d, id: null, preview: "" });
        }
    });
    return out.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
}

export const openFollowUps = (note) => ((note && note.follow_ups) || []).filter((f) => !f.done).length;

// Records linked in a note (distinct).
export const linkedCount = (note) => groupMentions(note && note.mentions).length;
